import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { TestParameter } from '../waterTestStrip/types'
import { WaterTestParamsSelectors } from '../waterTestParams'
import { WaterTestParameterResult } from '../../types'

const loading = (state: RootState) => state.waterTestKitResults.loading
const error = (state: RootState) => state.waterTestKitResults.error
const data = (state: RootState) => state.waterTestKitResults.data || []

const makeLatestResult = createSelector([data], (items) => {
  const sorted = items.slice().sort((a, b) => {
    if (!a.uploadedAt || !b.uploadedAt) return 0
    if (a.uploadedAt! > b.uploadedAt!) return 1
    if (a.uploadedAt! < b.uploadedAt!) return -1
    return 0
  })
  return sorted[0]
})
const makeLatestResultsSummaryShort = createSelector([makeLatestResult], (item) => {
  const parameters = (item && item.result && item.result.parameters) || {}
  const keys = Object.keys(parameters)
  if (keys.length === 0) return {}
  const summary = {} as any
  for (const k of keys) {
    const arr = parameters[k]
    arr.forEach((item) => {
      if (!summary[item.label]) {
        summary[item.label] = { count: 0, items: [] }
      }
      if (typeof summary[item.label][k] === 'undefined') {
        summary[item.label][k] = 0
      }
      summary[item.label].count += 1
      summary[item.label][k] += 1
      summary[item.label].items.push(item)
    })
  }
  return summary
})

type SummaryStatus = 'Caution' | 'High' | 'Low' | 'Good' | 'NA'
type ParameterCategories = 'Heavy Solids' | 'Chemical Byproducts' | 'Characteristics' | 'Other' | 'Chemical Additives'
type CategorizationStatusTitle = 'Action Required' | 'Keep Maintaining' | 'Monitor Closely' | 'Not Tracked' | ''
type ResultSummaryItems = {
  [key in ParameterCategories]: number
} & {
  count: number
  items: TestParameter[]
}
type ResultSummaryLong = {
  [key in SummaryStatus]: ResultSummaryItems
} & {
  categorizedSummary: {
    [key in CategorizationStatusTitle]: {
      count: number
      status: 'success' | 'warning' | 'error' | 'secondary'
      items: TestParameter[]
    }
  }
}

const makeLatestResultsSummaryLong = createSelector(
  [ makeLatestResultsSummaryShort],
  ( summary): ResultSummaryLong => {
    const categorizedSummary = Object.entries(summary).reduce((acc, [key, value]) => {
      let title, status
      if (key === 'NA') {
        title = 'Not Tracked'
        status = 'secondary'
      }
      if (['High', 'Low'].includes(key)) {
        title = 'Action Required'
        status = 'error'
      }

      if (key === 'Good') {
        title = 'Keep Maintaining'
        status = 'success'
      }
      if (key === 'Caution') {
        title = 'Monitor Closely'
        status = 'warning'
      }

      acc[title] = {
        status,
        count: acc[title] ? (acc[title].count += (value as any).count) : (value as any).count,
        items: acc[title]
          ? [...acc[title].items, ...(value as any).items]
          : [...(value as any).items] || [],
      }
      return acc
    }, {} as any)

    return {
      ...summary,
      categorizedSummary,
    }
  },
)

const makeLatestResultGroupedByParamGroup = createSelector(
  [
    makeLatestResult,
    WaterTestParamsSelectors.makeGroupedParams,
  ],
  (latestResult, groupedParams) => {
    if(!latestResult) return null
    const {result = {}} = latestResult
    const {parameters} = result || {} as any
    if(!parameters) return null
    const flatMap = Object.keys(parameters).reduce((acc, k): Array<WaterTestParameterResult & {category: string}> => {
      const arr = parameters[k]
      const normal = arr.map(item=> ({...item, category: k}))
      return [...acc,...normal]
    },[] as Array<WaterTestParameterResult & {category: string}>)
    const grouped = Object.keys(groupedParams).reduce((acc, k) => {
      const item = groupedParams[k]
      const { params} = item
      const paramItems = flatMap.filter(item => params.includes(item.name))
      if(!acc[k]){ acc[k] = {count:0, items:[],...item, group: k}}
      acc[k].count +=1
      acc[k].items = [...acc[k].items, ...paramItems]
      return acc
    }, {})
    return grouped

  }
)

export const WaterTestKitResultsSelector = {
  loading,
  error,
  data,
  makeLatestResult,
  makeLatestResultsSummaryShort,
  makeLatestResultsSummaryLong,
  makeLatestResultGroupedByParamGroup
}
